/**
 * Cutscene utility functions for export, import, and validation
 */

import { CutsceneData, CutsceneEvent, createEmptyCutscene } from "./types";

/**
 * Export cutscene data to JSON string
 */
export function exportCutsceneToJSON(cutscene: CutsceneData): string {
  return JSON.stringify(cutscene, null, 2);
}

/**
 * Download cutscene as JSON file
 */
export function downloadCutsceneJSON(cutscene: CutsceneData): void {
  const json = exportCutsceneToJSON(cutscene);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${cutscene.name.replace(/\s+/g, "-").toLowerCase()}.cutscene.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parse and validate cutscene JSON
 */
export function parseCutsceneJSON(json: string): CutsceneData {
  const data = JSON.parse(json);

  // Basic validation
  if (!data.id || typeof data.id !== "string") {
    throw new Error("Invalid cutscene: missing or invalid id");
  }
  if (!data.name || typeof data.name !== "string") {
    throw new Error("Invalid cutscene: missing or invalid name");
  }
  if (!data.mapId || typeof data.mapId !== "string") {
    throw new Error("Invalid cutscene: missing or invalid mapId");
  }
  if (!data.camera || typeof data.camera !== "object") {
    throw new Error("Invalid cutscene: missing or invalid camera config");
  }
  if (!Array.isArray(data.events)) {
    throw new Error("Invalid cutscene: events must be an array");
  }

  // Validate each event has required fields
  for (let i = 0; i < data.events.length; i++) {
    const event = data.events[i];
    if (!event.id || !event.type) {
      throw new Error(`Invalid event at index ${i}: missing id or type`);
    }
    if (typeof event.blocking !== "boolean") {
      // Default to true if not specified
      event.blocking = true;
    }
  }

  return data as CutsceneData;
}

/**
 * Import cutscene from file input
 */
export function importCutsceneFromFile(file: File): Promise<CutsceneData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const json = e.target?.result as string;
        const cutscene = parseCutsceneJSON(json);
        resolve(cutscene);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
}

/**
 * Clone a cutscene (deep copy)
 */
export function cloneCutscene(cutscene: CutsceneData): CutsceneData {
  return JSON.parse(JSON.stringify(cutscene));
}

/**
 * Clone an event (deep copy)
 */
export function cloneEvent<T extends CutsceneEvent>(event: T): T {
  return JSON.parse(JSON.stringify(event));
}

/**
 * Get display name for event type
 */
export function getEventTypeName(type: CutsceneEvent["type"]): string {
  const names: Record<CutsceneEvent["type"], string> = {
    spawn_pokemon: "Spawn Pokemon",
    move_pokemon: "Move Pokemon",
    animate_pokemon: "Animate Pokemon",
    remove_pokemon: "Remove Pokemon",
    play_vfx: "Play VFX",
    stop_vfx: "Stop VFX",
    camera_pan: "Camera Pan",
    camera_zoom: "Camera Zoom",
    camera_shake: "Camera Shake",
    dialog: "Show Dialog",
    hide_dialog: "Hide Dialog",
    wait: "Wait",
    fade: "Fade",
  };
  return names[type] || type;
}

/**
 * Get icon/color for event type (for timeline visualization)
 */
export function getEventTypeStyle(type: CutsceneEvent["type"]): {
  color: string;
  icon: string;
} {
  const styles: Record<CutsceneEvent["type"], { color: string; icon: string }> =
    {
      spawn_pokemon: { color: "#4ade80", icon: "+" },
      move_pokemon: { color: "#60a5fa", icon: "→" },
      animate_pokemon: { color: "#a78bfa", icon: "▶" },
      remove_pokemon: { color: "#f87171", icon: "×" },
      play_vfx: { color: "#fbbf24", icon: "✦" },
      stop_vfx: { color: "#fb923c", icon: "◼" },
      camera_pan: { color: "#22d3ee", icon: "⤢" },
      camera_zoom: { color: "#2dd4bf", icon: "⊕" },
      camera_shake: { color: "#f472b6", icon: "〰" },
      dialog: { color: "#e879f9", icon: "💬" },
      hide_dialog: { color: "#c084fc", icon: "✕" },
      wait: { color: "#94a3b8", icon: "⏱" },
      fade: { color: "#64748b", icon: "◐" },
    };
  return styles[type] || { color: "#94a3b8", icon: "?" };
}

/**
 * Calculate estimated duration of cutscene
 */
export function calculateCutsceneDuration(cutscene: CutsceneData): number {
  let totalDuration = 0;

  for (const event of cutscene.events) {
    if (!event.blocking) continue; // Non-blocking events don't add to duration

    switch (event.type) {
      case "move_pokemon":
      case "camera_pan":
      case "camera_zoom":
      case "camera_shake":
      case "wait":
      case "fade":
        totalDuration += event.duration;
        break;
      case "dialog":
        totalDuration += event.duration;
        break;
      case "hide_dialog":
        totalDuration += event.duration || 0;
        break;
      case "remove_pokemon":
        totalDuration += event.fadeOutDuration || 0;
        break;
      case "animate_pokemon":
        totalDuration += event.duration || 0;
        break;
      // Instant events
      case "spawn_pokemon":
      case "play_vfx":
      case "stop_vfx":
        break;
    }
  }

  return totalDuration;
}

/**
 * Format duration in ms to readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toFixed(0).padStart(2, "0")}`;
}

/**
 * Get list of actor IDs used in cutscene
 */
export function getActorIds(cutscene: CutsceneData): string[] {
  const actorIds = new Set<string>();

  for (const event of cutscene.events) {
    if ("actorId" in event && event.actorId) {
      actorIds.add(event.actorId);
    }
  }

  return Array.from(actorIds);
}

/**
 * Get list of VFX IDs used in cutscene
 */
export function getVFXIds(cutscene: CutsceneData): string[] {
  const vfxIds = new Set<string>();

  for (const event of cutscene.events) {
    if (event.type === "play_vfx") {
      vfxIds.add(event.vfxId);
    }
  }

  return Array.from(vfxIds);
}

/**
 * Reorder events in cutscene
 */
export function reorderEvents(
  cutscene: CutsceneData,
  fromIndex: number,
  toIndex: number
): CutsceneData {
  const newEvents = [...cutscene.events];
  const [removed] = newEvents.splice(fromIndex, 1);
  newEvents.splice(toIndex, 0, removed);

  return {
    ...cutscene,
    events: newEvents,
  };
}

/**
 * Insert event at index
 */
export function insertEvent(
  cutscene: CutsceneData,
  event: CutsceneEvent,
  index: number
): CutsceneData {
  const newEvents = [...cutscene.events];
  newEvents.splice(index, 0, event);

  return {
    ...cutscene,
    events: newEvents,
  };
}

/**
 * Remove event at index
 */
export function removeEvent(
  cutscene: CutsceneData,
  index: number
): CutsceneData {
  const newEvents = [...cutscene.events];
  newEvents.splice(index, 1);

  return {
    ...cutscene,
    events: newEvents,
  };
}

/**
 * Update event at index
 */
export function updateEvent(
  cutscene: CutsceneData,
  index: number,
  updates: Partial<CutsceneEvent>
): CutsceneData {
  const newEvents = [...cutscene.events];
  newEvents[index] = { ...newEvents[index], ...updates } as CutsceneEvent;

  return {
    ...cutscene,
    events: newEvents,
  };
}





























